
import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export interface Column {
  key: string;
  header: string;
  accessorKey?: string;
  render?: (value: any, row: any) => React.ReactNode;
  cell?: (props: any) => React.ReactNode;
  filterType?: "text" | "number" | "select";
  filterOptions?: string[];
  sortable?: boolean;
}

interface CustomizableTableProps {
  title?: string;
  columns: Column[];
  data: any[];
  onRowClick?: (row: any) => void;
  emptyMessage?: string;
  className?: string;
  isLoading?: boolean;
  filters?: Record<string, any>;
  onFilterChange?: (columnKey: string, value: any) => void;
  sortBy?: { key: string; direction: "asc" | "desc" | null };
  onSortChange?: (key: string) => void;
}

const CustomizableTable: React.FC<CustomizableTableProps> = ({
  title,
  columns,
  data,
  onRowClick,
  emptyMessage = "No data available",
  className,
  isLoading = false,
  filters = {},
  onFilterChange,
  sortBy,
  onSortChange,
}) => {
  const getSortIcon = (key: string) => {
    if (!sortBy || sortBy.key !== key || !sortBy.direction) return (
      <span className="inline-block ml-1 text-xs text-muted-foreground">&#8597;</span>
    );
    return sortBy.direction === "asc"
      ? <span className="inline-block ml-1 text-xs">&#8593;</span>
      : <span className="inline-block ml-1 text-xs">&#8595;</span>;
  };

  return (
    <div className={`${className || ''}`}>
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead
                key={column.key}
                className={column.sortable ? "cursor-pointer select-none font-medium whitespace-nowrap" : "font-medium whitespace-nowrap"}
                onClick={column.sortable && onSortChange ? () => onSortChange(column.key) : undefined}
              >
                <div className="flex items-center">
                  <span>{column.header}</span>
                  {column.sortable && getSortIcon(column.key)}
                </div>
              </TableHead>
            ))}
          </TableRow>
          {onFilterChange &&
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.key} className="whitespace-nowrap">
                  {column.filterType === "text" && (
                    <input
                      type="text"
                      value={filters[column.key] || ""}
                      onChange={e => onFilterChange(column.key, e.target.value)}
                      className="w-full text-sm px-2 py-1 border rounded bg-muted/30"
                      placeholder={`Filter ${column.header}`}
                    />
                  )}
                  {column.filterType === "number" && (
                    <input
                      type="number"
                      value={filters[column.key] || ""}
                      onChange={e => onFilterChange(column.key, e.target.value)}
                      className="w-full text-sm px-2 py-1 border rounded bg-muted/30"
                      placeholder={`Filter ${column.header}`}
                    />
                  )}
                  {column.filterType === "select" && Array.isArray(column.filterOptions) && (
                    <select
                      value={filters[column.key] || ""}
                      onChange={e => onFilterChange(column.key, e.target.value)}
                      className="w-full text-sm px-2 py-1 border rounded bg-muted/30"
                    >
                      <option value="">All</option>
                      {column.filterOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  )}
                </TableCell>
              ))}
            </TableRow>
          }
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="text-center h-32 text-muted-foreground"
              >
                <div className="flex justify-center items-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              </TableCell>
            </TableRow>
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="text-center h-32 text-muted-foreground"
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            data.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                onClick={() => onRowClick && onRowClick(row)}
                className={onRowClick ? "cursor-pointer hover:bg-muted/50" : ""}
              >
                {columns.map((column, colIdx) => (
                  <TableCell
                    key={`${rowIndex}-${column.key}`}
                    className={column.key === "actions" ? "text-right w-0 whitespace-nowrap" : "whitespace-nowrap"}
                  >
                    {column.render
                      ? column.render(column.accessorKey ? row[column.accessorKey] : null, row)
                      : column.cell
                      ? column.cell({ row, getValue: () => column.accessorKey ? row[column.accessorKey] : null })
                      : column.accessorKey
                      ? row[column.accessorKey]
                      : null}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default CustomizableTable;
